import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import InputMask from 'react-input-mask';
import ObservacaoModal from './ObservacaoModal';
import Button from '../common/Button'; // Importando o botão genérico
import './PatientEditModal.css';
import { differenceInHours, parseISO, isValid } from 'date-fns';

// Função para formatar a data
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0]; // Formato yyyy-mm-dd
};

function PatientEditModal({ isOpen, onClose, paciente, onSave }) {
  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    telefone: '',
    email: '',
    endereco: '',
    clinica: '',
    plano: '',
    dataNascimento: '',
    dataAtendimento: '',
    observacoes: '',
  });
  const [isObservacaoModalOpen, setIsObservacaoModalOpen] = useState(false);
  const [isEditableAnamnese, setIsEditableAnamnese] = useState(true);
  const [remainingTime, setRemainingTime] = useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (paciente) {
      setFormData({
        nome: paciente.nome || '',
        cpf: paciente.cpf || '',
        telefone: paciente.telefone || '',
        email: paciente.email || '',
        endereco: paciente.endereco || '',
        clinica: paciente.clinica || '',
        plano: paciente.plano || '',
        dataNascimento: paciente.dataNascimento
          ? formatDate(paciente.dataNascimento)
          : '',
        dataAtendimento: paciente.dataAtendimento
          ? formatDate(paciente.dataAtendimento)
          : '',
        observacoes: paciente.observacoes || '',
      });

      // Verifica se a data de atendimento está definida e é válida
      if (paciente.dataAtendimento) {
        const atendimentoDate = parseISO(paciente.dataAtendimento);

        if (isValid(atendimentoDate)) {
          const hoursPassed = differenceInHours(new Date(), atendimentoDate);

          if (hoursPassed >= 24) {
            setIsEditableAnamnese(false);
            setRemainingTime(0);
          } else {
            setIsEditableAnamnese(true);
            setRemainingTime(24 - hoursPassed);
          }
        } else {
          setIsEditableAnamnese(false);
          setRemainingTime(0);
        }
      } else {
        setIsEditableAnamnese(false);
        setRemainingTime(0);
      }
    }
  }, [paciente]);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen || !paciente) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleOpenObservacaoModal = () => {
    setIsObservacaoModalOpen(true); // Abrir o modal de observação
  };

  const handleCloseObservacaoModal = () => {
    setIsObservacaoModalOpen(false); // Fechar o modal de observação
  };

  const handleObservacaoChange = (value) => {
    setFormData({ ...formData, observacoes: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div
      className="patient-edit-modal-overlay"
      aria-modal="true"
      role="dialog"
      aria-labelledby="edit-modal-title"
      ref={modalRef}
      tabIndex="-1"
    >
      <div className="patient-edit-modal-content">
        <button
          className="patient-edit-modal-close-button"
          onClick={onClose}
          aria-label="Fechar Modal"
        >
          &times;
        </button>
        <h2 id="edit-modal-title">Editar Paciente</h2>

        {remainingTime !== null && isEditableAnamnese && (
          <div className="text-gray-500 text-sm mb-4">
            Tempo restante para editar a Anamnese: {remainingTime} horas
          </div>
        )}

        {!isEditableAnamnese && (
          <div className="text-red-500 text-sm mb-4">
            O prazo de 24 horas para editar a Anamnese expirou.
          </div>
        )}

        <form onSubmit={handleSubmit} className="patient-edit-modal-form">
          <div className="patient-edit-modal-grid">
            <div className="patient-edit-modal-form-group">
              <label htmlFor="nome">Nome:</label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
              />
            </div>
            <div className="patient-edit-modal-form-group">
              <label htmlFor="cpf">CPF:</label>
              <InputMask
                mask="999.999.999-99"
                value={formData.cpf}
                onChange={handleInputChange}
              >
                {(inputProps) => (
                  <input {...inputProps} type="text" id="cpf" name="cpf" />
                )}
              </InputMask>
            </div>
            <div className="patient-edit-modal-form-group">
              <label htmlFor="telefone">Telefone:</label>
              <InputMask
                mask={
                  formData.telefone.length > 10
                    ? '(99) 9.9999-9999'
                    : '(99) 9999-9999'
                }
                value={formData.telefone}
                onChange={handleInputChange}
              >
                {(inputProps) => (
                  <input
                    {...inputProps}
                    type="text"
                    id="telefone"
                    name="telefone"
                  />
                )}
              </InputMask>
            </div>
            <div className="patient-edit-modal-form-group">
              <label htmlFor="email">Email:</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>
            <div className="patient-edit-modal-form-group">
              <label htmlFor="endereco">Endereço:</label>
              <input
                type="text"
                id="endereco"
                name="endereco"
                value={formData.endereco}
                onChange={handleInputChange}
              />
            </div>
            <div className="patient-edit-modal-form-group">
              <label htmlFor="clinica">Clínica:</label>
              <input
                type="text"
                id="clinica"
                name="clinica"
                value={formData.clinica}
                onChange={handleInputChange}
              />
            </div>
            <div className="patient-edit-modal-form-group">
              <label htmlFor="plano">Plano de Saúde:</label>
              <input
                type="text"
                id="plano"
                name="plano"
                value={formData.plano}
                onChange={handleInputChange}
              />
            </div>
            <div className="patient-edit-modal-form-group">
              <label htmlFor="dataNascimento">Data de Nascimento:</label>
              <input
                type="date"
                id="dataNascimento"
                name="dataNascimento"
                value={formData.dataNascimento}
                onChange={handleInputChange}
              />
            </div>
            <div className="patient-edit-modal-form-group">
              <label htmlFor="dataAtendimento">Data de Atendimento:</label>
              <input
                type="date"
                id="dataAtendimento"
                name="dataAtendimento"
                value={formData.dataAtendimento}
                disabled
              />
            </div>
            <div className="patient-edit-modal-form-group patient-edit-modal-full-width">
              <label htmlFor="observacoes">Anamnese/Observações:</label>
              <button
                type="button"
                className={`px-6 py-2.5 rounded-lg shadow-md text-base font-medium transition transform duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-opacity-50 active:scale-95 bg-gradient-to-r rounded-3x1 from-primary to-primary-hover text-white hover:from-primary-hover hover:to-secondary ${
                  !isEditableAnamnese ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                onClick={handleOpenObservacaoModal}
                disabled={!isEditableAnamnese} // Condição de desabilitação
              >
                Editar Anamnese
              </button>
            </div>
          </div>

          <button type="submit" className="patient-edit-modal-submit-button">
            Salvar
          </button>
        </form>
        {isObservacaoModalOpen && (
          <ObservacaoModal
            isOpen={isObservacaoModalOpen}
            onClose={handleCloseObservacaoModal}
            value={formData.observacoes}
            onChange={handleObservacaoChange}
            callerComponent="PatientEditModal"
            isReadOnly={!isEditableAnamnese}
          />
        )}
      </div>
    </div>
  );
}

PatientEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  paciente: PropTypes.shape({
    nome: PropTypes.string,
    cpf: PropTypes.string,
    telefone: PropTypes.string,
    email: PropTypes.string,
    endereco: PropTypes.string,
    clinica: PropTypes.string,
    plano: PropTypes.string,
    dataNascimento: PropTypes.string,
    dataAtendimento: PropTypes.string,
    observacoes: PropTypes.string,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
};

export default PatientEditModal;
